import { useTranslation } from 'react-i18next';
import { SelectOption } from '@anytag/shared-types';
import { SetValueConfig, get } from 'react-hook-form';
import { ErrorMessage, Select, SelectProps, Slider, SliderProps } from '@anytag/shared-atoms';
import { RHFFieldWrapper } from './shared';
import { DefaultFieldProps } from './types';
import { useFormatRHFFieldProps } from './hooks';

interface Field<T extends string, TOption extends SelectOption<string>>
  extends Pick<SelectProps<TOption>, 'disabled' | 'placeholder' | 'clearable'> {
  name: T;
}

export interface RangeSliderInputFieldProps<
  T extends string = string,
  TOption extends SelectOption<string> = SelectOption<string>
> extends Omit<DefaultFieldProps<T, string>, 'name'> {
  options: TOption[];
  hideSlider?: boolean;
  onSubmit?: () => void;
  minField: Field<T, TOption>;
  maxField: Field<T, TOption>;
  setValueOptions?: SetValueConfig;
  slideProps?: Pick<SliderProps, 'step' | 'min' | 'max'>;
  customSelectedLabel?: SelectProps<TOption>['customSelectedLabel'];
}

export const RangeSliderInputField = <T extends string = string>(props: RangeSliderInputFieldProps<T>) => {
  const { t } = useTranslation();
  const {
    setValueOptions,
    fieldWrapperProps,
    formContext: {
      watch,
      setValue,
      formState: { errors }
    },
    fieldProps: {
      onChange,
      minField,
      maxField,
      disabled,
      options,
      onSubmit,
      slideProps,
      hideSlider,
      customSelectedLabel
    }
  } = useFormatRHFFieldProps({ ...props, name: '' });

  // minValue | maxValue mixed string|number type
  const [minValue, maxValue] = watch([minField.name, maxField.name]);
  const minDisabled = disabled || minField.disabled;
  const maxDisabled = disabled || maxField.disabled;
  const minOptions = maxValue !== '' ? options.filter((o) => Number(o.value) < Number(maxValue)) : options;
  const maxOptions = minValue !== '' ? options.filter((o) => Number(o.value) > Number(minValue)) : options;

  // Slider value might not match with options because of different step, so we fallback to raw value
  const formatValue = (value: string | number | undefined, opts: typeof options) =>
    value !== '' ? opts.find((o) => Number(o.value) === Number(value))?.value ?? String(value) : '';

  const handleChange = (name: T) => (value: string) => {
    if (onChange) {
      onChange(name, value);
    } else {
      setValue<string>(name, value, setValueOptions);
    }
    onSubmit?.();
  };

  const handleSliderChange: SliderProps['onValueChange'] = (value) => {
    if (typeof value === 'object') {
      if (!minDisabled) {
        handleChange(minField.name)(value.at(0)?.toString() || '');
      }
      if (!maxDisabled) {
        handleChange(maxField.name)(value.at(1)?.toString() || '');
      }
    }
  };

  return (
    <RHFFieldWrapper {...fieldWrapperProps} hideError>
      <>
        {!hideSlider ? (
          <Slider
            step={1}
            {...slideProps}
            disabled={disabled}
            className="mx-0.5 mb-2.75"
            aria-labelledby="range-slider"
            onValueChange={handleSliderChange}
            value={[Number(minValue), Number(maxValue)]}
            defaultValue={[Number(minValue), Number(maxValue)]}
          />
        ) : null}

        <div className="box-border flex items-center">
          <Select
            className="flex-1"
            options={minOptions}
            disabled={minDisabled}
            clearable={minField.clearable}
            placeholder={minField.placeholder}
            onChange={handleChange(minField.name)}
            customSelectedLabel={customSelectedLabel}
            value={formatValue(minValue, minOptions)}
          />
          <span className="mx-2 my-auto text-sm font-normal text-primary">〜</span>
          <Select
            className="flex-1"
            options={maxOptions}
            disabled={maxDisabled}
            clearable={maxField.clearable}
            placeholder={maxField.placeholder}
            onChange={handleChange(maxField.name)}
            customSelectedLabel={customSelectedLabel}
            value={formatValue(maxValue, maxOptions)}
          />
        </div>

        <ErrorMessage message={t<string>(get(errors, minField.name) || get(errors, maxField.name) || '')} />
      </>
    </RHFFieldWrapper>
  );
};
